import { parseISO } from 'date-fns';
import { getUserPreferences } from '@chr/online-shared-microsite-core';
import { toLocaleDateTime, toLocaleNumber } from '@chr/online-shared-ui-localizations';

import {
  Address,
  CargoSummary,
  Dimension,
  DimensionUom,
  Location,
  NonPurchaseOrderItem, PackageUom,
  TemperatureUom,
  VolumeUom,
  WeightUom
} from '@/common/apis/dtos';
import { DocumentType, DocumentTypeDisplay } from '@/common/constants/document-types';
import { getRollupBookingStatusLabel } from '@/common/utilities/booking';

export type TranslateType = (key: string, replaceValues?: Stringable[]) => string;
export type Stringable = | string | number | boolean | { toString(): string };

const intlDateOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric'
};

const intlTimeOptions: Intl.DateTimeFormatOptions = { timeStyle: 'short' };

export function parseToISODate(dateTime: string, withTz = false) {
  const newDate = new Date(withTz ? dateTime : dateTime.substring(0, 19));
  const ISODateString = newDate.toISOString();

  return parseISO(ISODateString);
}

export const IsoToDate = (
  isoDateString: string | null | undefined,
  defaultDate = '-',
  dateOptions: Intl.DateTimeFormatOptions = intlDateOptions
): string => {
  if (isoDateString && isoDateString?.length > 1) {
    const date = parseToISODate(isoDateString);
    return toLocaleDateTime(date, dateOptions);
  }

  return defaultDate;
};

export const IsoToTime = (isoDateTimeString: string | null | undefined, missingTimeStr = '-', withTz = false): string => {
  let formattedTime = missingTimeStr;

  if (isoDateTimeString && isoDateTimeString?.length > 1) {
    const date = parseToISODate(isoDateTimeString, withTz);
    formattedTime = toLocaleDateTime(date, intlTimeOptions);
  }

  return formattedTime;
};

export const IsoToDateAtTime = (
  isoDate: string | undefined,
  translate: (key: string, replaceValues: Stringable[]) => string,
  withoutTime?: boolean
): string => {
  if (!isoDate) {
    return '';
  }

  const convertedDate: string = isoDate ? IsoToDate(isoDate) : '';
  const convertedTime: string = isoDate ? IsoToTime(isoDate) : '';

  if (withoutTime && convertedDate.length > 1) {
    return translate('{0}', [convertedDate]);
  }

  return convertedDate.length > 1 && convertedTime.length > 1
    ? translate('{0} at {1}', [convertedDate, convertedTime])
    : '';
};

/**
 * @description Convert an ISO string with timezone to a locale date at time string.
 * @param isoDateString ISO string with timezone (e.g '2020-12-31T00:01:00-05:00')
 * @param translate translate function
 * @param withoutTime ignore time portion
 * @returns locale `date at time` string (e.g '12/31/2020 at 12:01 AM')
 */
export const IsoStringToLocaleDateAtTime = (
  isoDateString: string | undefined,
  translate: (key: string, replaceValues: Stringable[]) => string,
  withoutTime?: boolean): string => {
  if (!isoDateString) {
    return '';
  }

  const convertedDate = toLocaleDateTime(new Date(isoDateString), intlDateOptions);

  if (withoutTime) {
    return translate('{0}', [convertedDate]);
  }

  const convertedTime = toLocaleDateTime(parseISO(isoDateString), intlTimeOptions);

  return convertedDate.length > 1 && convertedTime.length > 1
    ? translate('{0} at {1}', [convertedDate, convertedTime])
    : '';
};

export const toKebabCase = (label: string) => {
  const x = label.match(
    /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g
  );
  if (x && x.length > 0) {
    return x.map((x) => x.toLowerCase()).join('-');
  }
};

export const getDocumentTypeDisplayName = (type: DocumentType | undefined) => {
  if (type !== undefined) {
    return DocumentTypeDisplay[type];
  } else {
    return 'Other';
  }
};

export const formatAddress = (address: Address) => {
  const addressElements: string[] = [removeExtraSpace(address.city, '-')];
  const stateProvince = address.stateProvince;
  const country = address.country;

  // Most of the time country and stateProvince will be the same. This logic is in shipments UI and is equivalent. We
  // only show the country if the province/state is the same as the country.
  if (stateProvince && country && stateProvince === country) {
    addressElements.push(removeExtraSpace(country, '-'));
  } else {
    addressElements.push(removeExtraSpace(stateProvince, '-'));
    addressElements.push(removeExtraSpace(country, '-'));
  }

  return addressElements.filter(e => !!e).join(', ');
};

export const formatLocation = (location: Location) => {
  return formatAddress(location.physicalAddress ?? {});
};

export const getRollupBookingStatusLabelOrDefault =
  (translate: (key: string, replaceValues?: Stringable[]) => string, bookingStatusId?: number) => {
    if (!bookingStatusId) {
      return '-';
    }
    const mappedBookingStatus = getRollupBookingStatusLabel(bookingStatusId);

    return mappedBookingStatus ? translate(mappedBookingStatus) : '-';
  };

export const boolToYesNo = (value: boolean | undefined) => value ? 'Yes' : 'No';

export const removeExtraSpace = (value: string | undefined | null, defaultString: string): string => {
  return value && (value.trim().length !== 0) ? value.trim() : defaultString;
};

export const formatNumber = (value: number | null | undefined, fixedTo?: number, defaultString?: string): string => {
  if ((value === undefined || value === null) && !!defaultString) {
    return defaultString;
  }

  const formattedValue = !value ? 0 : value;
  const decimalPlaces = !fixedTo ? 0 : fixedTo;

  return toLocaleNumber(formattedValue, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
};

export const kgToLb = (kg: number) => kg * 2.20462;
export const lbToKg = (lb: number) => lb * 0.453592;
export const cbmToCft = (cbm: number) => cbm * 35.3147;
export const cftToCbm = (cft: number) => cft * 0.0283168;
export const cmToIn = (cm: number) => cm * 0.393701;
export const inToCm = (inch: number) => inch * 2.54;
export const fToC = (f: number) => (f - 32) * 5/9;
export const cToF = (c: number) => c * 9/5 + 32;

// These functions check to see if there is a mismatch in units, and convert to the user's preference,
// else return identical value.
export const convertTemperature = (temp?: number, unit?: TemperatureUom | number, preference: System = getUserPreferencesSystemOfMeasure()) => {
  if (!temp) {
    return 0;
  }

  if (preference === System.standard && (unit === TemperatureUom.Celsius || unit === Measurements.TemperatureUnit.Celsius)) {
    return cToF(temp);
  }

  if (preference === System.metric && (unit === TemperatureUom.Fahrenheit || unit === Measurements.TemperatureUnit.Fahrenheit)) {
    return fToC(temp);
  }

  return temp;
};

export const convertDimension = (distance?: number, unit?: DimensionUom | number, preference: System = getUserPreferencesSystemOfMeasure()) => {
  if (!distance) {
    return 0;
  }

  if (preference === System.standard && (unit === DimensionUom.Cm || unit === Measurements.LinearUnit.cm)) {
    return cmToIn(distance);
  }

  if (preference === System.metric && (unit === DimensionUom.In || unit === Measurements.LinearUnit.in)) {
    return inToCm(distance);
  }

  return distance;
};

export const convertWeight = (weight?: number, unit?: WeightUom | number, preference: System = getUserPreferencesSystemOfMeasure()) => {
  if (!weight) {
    return 0;
  }

  if (preference === System.standard && (unit === WeightUom.Kgs || unit === Measurements.WeightUnit.kg)) {
    return kgToLb(weight);
  }

  if (preference === System.metric && (unit === WeightUom.Lbs || unit === Measurements.WeightUnit.lbs)) {
    return lbToKg(weight);
  }

  return weight;
};

// Checks to see if there is a mismatch in units, and convert to the user's preference, else return identical value
export const convertVolume = (volume?: number, unit?: VolumeUom | number, preference: System = getUserPreferencesSystemOfMeasure()) => {
  if (!volume) {
    return 0;
  }

  if (preference === System.standard && (unit === VolumeUom.Cbm || unit === Measurements.VolumeUnit.Cbm)) {
    return cbmToCft(volume);
  }


  if (preference === System.metric && (unit === VolumeUom.Cft || unit === Measurements.VolumeUnit.Cft)) {
    return cftToCbm(volume);
  }

  return volume;
};

export enum System {
  standard = 3098,
  metric = 3099,
}

export class Measurements {
  static readonly LinearUnit = {
    in: 1278,
    ft: 1279,
    cm: 1280,
    m: 1281
  };

  static readonly WeightUnit = {
    lbs: 1283,
    kg: 1284
  };

  static readonly TemperatureUnit = {
    Fahrenheit: 1286,
    Celsius: 1287
  };

  static readonly VolumeUnit = {
    Cbm: 1290,
    Cft: 1289
  };

  static readonly PackagingTypeUnit = {
    Bag: 277,
    Bundle: 279,
    Box: 282,
    Case: 284,
    Crate: 285,
    Carton: 286,
    Drum: 287,
    LoosePiece: 289,
    Pail: 292,
    Package: 294,
    Pallet: 295,
    Roll: 297,
    Skid: 299,
    Envelope: 5195,
    Gaylord: 5196,
    JerryCan: 5198,
    Tube: 5212
  };

  static getPackagingTypeLabel = (rdn?: number): string | null => {
    switch (rdn) {
      case Measurements.PackagingTypeUnit.Bag:
        return 'Bag';
      case Measurements.PackagingTypeUnit.Bundle:
        return 'Bundle';
      case Measurements.PackagingTypeUnit.Box:
        return 'Box';
      case Measurements.PackagingTypeUnit.Case:
        return 'Case';
      case Measurements.PackagingTypeUnit.Crate:
        return 'Crate';
      case Measurements.PackagingTypeUnit.Carton:
        return 'Carton';
      case Measurements.PackagingTypeUnit.Drum:
        return 'Drum';
      case Measurements.PackagingTypeUnit.LoosePiece:
        return 'Loose Piece';
      case Measurements.PackagingTypeUnit.Pail:
        return 'Pail';
      case Measurements.PackagingTypeUnit.Package:
        return 'Package';
      case Measurements.PackagingTypeUnit.Pallet:
        return 'Pallet';
      case Measurements.PackagingTypeUnit.Roll:
        return 'Roll';
      case Measurements.PackagingTypeUnit.Skid:
        return 'Skid';
      case Measurements.PackagingTypeUnit.Envelope:
        return 'Envelope';
      case Measurements.PackagingTypeUnit.Gaylord:
        return 'Gaylord';
      case Measurements.PackagingTypeUnit.JerryCan:
        return 'Jerry Can';
      case Measurements.PackagingTypeUnit.Tube:
        return 'Tube';
      default:
        return null;
    }
  };

  static getTemperatureLabelPref = (unitPref: System): string => unitPref === System.standard ? '\u00B0F' : '\u00B0C';
  static getDimensionLabelPref = (unitPref: System): string => unitPref === System.standard ? 'in' : 'cm';
  static getWeightLabelPref = (unitPref: System): string => unitPref === System.standard ? 'lbs' : 'kg';
  static getWeightRdnPref = (unitPref: System): number => unitPref === System.standard ? Measurements.WeightUnit.lbs : Measurements.WeightUnit.kg;
  static getVolumeRdnPref = (unitPref: System): number => unitPref === System.standard ? Measurements.VolumeUnit.Cft : Measurements.VolumeUnit.Cbm;
  static getVolumeLabelPref = (unitPref: System): string => unitPref === System.standard ? 'cft' : 'cbm';
}

export function getUserPreferencesSystemOfMeasure(): System {
  return getUserPreferences()?.unitOfMeasure === 'metric' ? System.metric : System.standard;
}

export const formatDimensions = (dimensions: Dimension[] | undefined, dimensionUom: DimensionUom, dimLabelPref: string, volumeLabelPref: string) => {
  return dimensions?.map((dim) => ({
    L: (dim?.length ?? 0) > 0 ? `${formatNumber(convertDimension(dim.length ?? 0, dimensionUom), 0, '-')} ${dimLabelPref}` : '-',
    W: (dim?.width ?? 0) > 0 ? `${formatNumber(convertDimension(dim.width ?? 0, dimensionUom), 0, '-')} ${dimLabelPref}` : '-',
    H: (dim?.height ?? 0) > 0 ? `${formatNumber(convertDimension(dim.height ?? 0, dimensionUom), 0, '-')} ${dimLabelPref}` : '-',
    Volume: (dim?.volume ?? 0) > 0 ? `${formatNumber(convertVolume(dim.volume ?? 0, dimensionUom), 2, '-')} ${volumeLabelPref}` : '-',
    Quantity: (dim?.quantity?.value ?? 0) > 0 ? `${formatNumber(dim.quantity?.value ?? 0, 0, '-')}` : '-',
  })) ?? [];
};

export const mergeItemsIntoStrings = (items: { [key: string]: string }[]) => {
  return items.map((item) => {
    const validItems = Object.entries(item).filter(([_, value]) => value !== '-');
    return validItems.map(([label, value]) => `${label}: ${value}`).join(', ');
  });
};

export const formatCargoSummaryProperties = (cargoSummary: CargoSummary) => {
  const userPrefUom = getUserPreferencesSystemOfMeasure();
  const weightLabelPref = Measurements.getWeightLabelPref(userPrefUom);
  const volumeLabelPref = Measurements.getVolumeLabelPref(userPrefUom);
  const dimLabelPref = Measurements.getDimensionLabelPref(userPrefUom);

  const outerPackQuantityValue = cargoSummary.quantity?.value ?? 0;
  const outerPackQtyUnit = PackageUom[cargoSummary.quantity?.unit ?? 0];
  const outerPackQty = outerPackQuantityValue > 0 ? `${formatNumber(outerPackQuantityValue, 0, '-')} ${outerPackQtyUnit}` : '-';

  const rawWeight = convertWeight(cargoSummary.totalWeight?.value, cargoSummary.totalWeight?.unit);
  const weight = rawWeight > 0 ? `${formatNumber(rawWeight, 0, '-')} ${weightLabelPref}` : '-';

  const rawVolume = convertVolume(cargoSummary.totalVolume?.value, cargoSummary.totalVolume?.unit);
  const volume = rawVolume > 0 ? `${formatNumber(rawVolume, 2, '-')} ${volumeLabelPref}` : '-';

  const dimensionUom =
    cargoSummary.totalVolume?.unit === VolumeUom.Cbm ||
    cargoSummary.totalWeight?.unit === WeightUom.Kgs
    ? DimensionUom.Cm : DimensionUom.In;

    const formattedDimensions = formatDimensions(cargoSummary.dimensions, dimensionUom, dimLabelPref, volumeLabelPref);

    return {
      outerPackQty,
      weight,
      volume,
      formattedDimensions,
    }
}

export const formatNonPoItemProperties = (item: NonPurchaseOrderItem)  => {
  const isHazmat = !!item.isHazMat;
  const isTempControlled = !!item.isTemperatureControlled;

  const userPrefUom = getUserPreferencesSystemOfMeasure();
  const weightLabelPref = Measurements.getWeightLabelPref(userPrefUom);
  const volumeLabelPref = Measurements.getVolumeLabelPref(userPrefUom);
  const dimLabelPref = Measurements.getDimensionLabelPref(userPrefUom);
  const tempLabelPref = Measurements.getTemperatureLabelPref(userPrefUom);

  const rawOuterPackQty = item.quantity?.value ?? 0;
  const outerPackQtyUnit = PackageUom[item.quantity?.unit ?? 0];
  const outerPackQty = rawOuterPackQty > 0 ? `${formatNumber(rawOuterPackQty, 0, '-')} ${outerPackQtyUnit}` : '-';

  const rawWeight = convertWeight(item.totalWeight?.value, item.totalWeight?.unit);
  const weight = rawWeight > 0 ? `${formatNumber(rawWeight, 0, '-')} ${weightLabelPref}` : '-';

  const rawVolume = convertVolume(item.totalVolume?.value, item.totalVolume?.unit);
  const volume = rawVolume > 0 ? `${formatNumber(rawVolume, 2, '-')} ${volumeLabelPref}` : '-';

  // The Gateway has no way of returning the dimension's Unit of Measure because the GF.Booking.API doesn't return this
  // information. Therefore, we have to deduce the dimension unit from the other properties.
  const dimensionUom =
    item.totalVolume?.unit === VolumeUom.Cbm ||
    item.totalWeight?.unit === WeightUom.Kgs ||
    item.maxTemperature?.unit === TemperatureUom.Celsius ? DimensionUom.Cm : DimensionUom.In;

  const formattedDimensions = formatDimensions(item.dimensions, dimensionUom, dimLabelPref, volumeLabelPref);

  const rawMaxTemp = convertTemperature(item.maxTemperature?.value, item.maxTemperature?.unit);
  const rawMinTemp = convertTemperature(item.minTemperature?.value, item.minTemperature?.unit);
  const maxTemp = rawMaxTemp > 0 ? `${formatNumber(rawMaxTemp, 0, '-')}${tempLabelPref}` : '-';
  const minTemp = rawMinTemp > 0 ? `${formatNumber(rawMinTemp, 0, '-')}${tempLabelPref}` : '-';

  const marks = item.marks || '-';
  const notes = item.commodity || '-';

  return {
    isHazmat,
    isTempControlled,
    outerPackQty,
    weight,
    volume,
    formattedDimensions,
    maxTemp,
    minTemp,
    marks,
    notes
  }
}
